'use client'

import React, { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import type { Communication } from '@shared/postui'
import { EntryDetailPanel, detailGridCols } from '../EntryDetail/EntryDetailPanel'
import { WidgetLibraryPicker } from '../EntryDetail/WidgetLibraryPicker'
import { WORKBENCH_WIDGET_LIBRARY, type SibsWidgetDefinition } from '../EntryDetail/widgetLibrary'
import { SibsEntryDataWidget, type SibsEntry } from '../EntryDetail/SibsWidgets'
import { UserFacingPreviewWidget } from '../EntryDetail/UserFacing/UserFacingPreviewWidget'
import { PipelineStepsWidget, type PipelineStepInput } from '../PipelineStepsWidget'
import { OriginalSubmissionWidget } from '../ProcessedEntry/OriginalSubmissionWidget'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { ActionableEvidencePanel, deriveActionableEvidence } from './AdminSubmitEntryView'
import { CollapsibleSection } from './CollapsibleSection'
import { PayloadWidget } from './PayloadWidget'
import type { Catalog } from './entryTypes'

// Every card in the "Generic" row shares this exact height (not just a cap)
// so the row reads as one uniform strip regardless of how much content each
// widget happens to have — short ones get empty space, long ones scroll
// internally, rather than the row's height being dictated by whichever
// widget has the most content.
export const GENERIC_ROW_HEIGHT = 'h-[420px]'

export function RefreshButton({ onRefresh, refreshing, theme }: { onRefresh: () => void; refreshing: boolean; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={refreshing}
      title="Refresh"
      className={`rounded p-1 transition-colors hover:text-sky-500 disabled:opacity-50 ${c.faint}`}
      aria-label="Refresh"
    >
      <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
    </button>
  )
}

// The "Generic" row every entry detail page shows — Cases/Unified/Campaign
// members (CaseEntryDetailPage), SIBS Custom Mail Alert + Alerts Analysis
// (SibsEntryDetailPage) and the Workbench (AdminSubmitEntryView). One
// component so a tile added here shows on all of them; the three hand-kept
// copies had drifted (SIBS never got "What the user sees" or Actionable
// Evidence). Page-specific tiles go in through the slots below rather than
// by forking the row again.
export function GenericEntryRow({
  theme,
  entry,
  catalog,
  steps,
  stepsReady,
  stepsLoading = false,
  stepsEmptyMessage,
  onRefresh,
  refreshing = false,
  submission,
  preview,
  payload,
  evidenceHeaderExtras,
  evidenceBody,
  busyBadge,
  extraPanels,
  library = WORKBENCH_WIDGET_LIBRARY,
  defaultOpen = false,
}: {
  theme: ProcessedTheme
  /** Entry Data tile + the "+ Add widget" library widgets. */
  entry: SibsEntry
  catalog: Catalog
  steps: PipelineStepInput[]
  /** False until the steps' first real load lands — see UserFacingPreviewWidget. */
  stepsReady: boolean
  stepsLoading?: boolean
  stepsEmptyMessage?: string
  onRefresh?: () => void
  refreshing?: boolean
  submission: {
    r2Key: string | null | undefined
    getToken: () => Promise<string>
    communication?: Communication
    postType?: string | null
    messageTypeId?: number | string | null
    messageTypeText?: string | null
  }
  /** "What the user sees" inputs. */
  preview: {
    raw: Record<string, any>
    processed?: Record<string, any> | null
    source?: string | null
    userEntryId?: string
  }
  /** Payload tile body; defaults to PayloadWidget over the entry's own payloads. */
  payload?: React.ReactNode
  /** Extra header buttons on Actionable Evidence (Workbench's raw/copy toggles). */
  evidenceHeaderExtras?: React.ReactNode
  /** Replaces the Actionable Evidence body (Workbench's raw JSON mode). */
  evidenceBody?: React.ReactNode
  /** Shown before the refresh button on Payload/Evidence while something reloads. */
  busyBadge?: React.ReactNode
  /** Page-specific tiles, placed after the shared ones (SIBS's Domains). */
  extraPanels?: React.ReactNode
  library?: SibsWidgetDefinition[]
  defaultOpen?: boolean
}) {
  const evidence = React.useMemo(() => deriveActionableEvidence(steps), [steps])
  const refreshButton = onRefresh ? <RefreshButton onRefresh={onRefresh} refreshing={refreshing} theme={theme} /> : null
  const headerRight = (busyBadge || refreshButton) ? <>{busyBadge}{refreshButton}</> : undefined

  // Session-only "+ Add widget" picks, cleared when the entry changes.
  const [appendedWidgets, setAppendedWidgets] = useState<{ instanceId: string; widgetId: string }[]>([])
  useEffect(() => { setAppendedWidgets([]) }, [entry.id])
  const addWidget = (widgetId: string) => {
    setAppendedWidgets((prev) => [...prev, { instanceId: crypto.randomUUID(), widgetId }])
  }
  const removeWidget = (instanceId: string) => {
    setAppendedWidgets((prev) => prev.filter((w) => w.instanceId !== instanceId))
  }

  return (
    <CollapsibleSection label="Generic" theme={theme} defaultOpen={defaultOpen}>
      <div className={`grid gap-3 ${detailGridCols(4)}`}>
        {/* Entry Data — the row's own fields; not the Payload tile below,
            which is original vs agentic-parsed *payload*. */}
        <EntryDetailPanel label="Entry Data" theme={theme} cols={2} scrollable className={GENERIC_ROW_HEIGHT}>
          <SibsEntryDataWidget entry={entry} theme={theme} catalog={catalog} />
        </EntryDetailPanel>

        {/* Self-wraps in its own Widget card, so not inside an EntryDetailPanel. */}
        <PipelineStepsWidget
          steps={steps}
          theme={theme}
          loading={stepsLoading}
          emptyMessage={stepsEmptyMessage}
          maxHeightClassName={GENERIC_ROW_HEIGHT}
          className="col-span-1"
          onRefresh={onRefresh}
          refreshing={refreshing}
        />

        <OriginalSubmissionWidget
          r2Key={submission.r2Key}
          theme={theme}
          getToken={submission.getToken}
          communication={submission.communication}
          postType={submission.postType}
          messageTypeId={submission.messageTypeId}
          messageTypeText={submission.messageTypeText}
          className={`col-span-1 ${GENERIC_ROW_HEIGHT}`}
        />

        <EntryDetailPanel label="Payload" theme={theme} cols={2} scrollable className={GENERIC_ROW_HEIGHT} headerRight={headerRight}>
          {payload ?? (
            <PayloadWidget
              originalPayload={entry.raw_entry_original_payload}
              parsedPayload={entry.raw_entry_parsed_payload}
              theme={theme}
            />
          )}
        </EntryDetailPanel>

        {/* What the reporter was shown by their public surface, rebuilt from
            the same pipeline steps the Pipeline Steps tile lists, so both
            refresh together. noPadding: the widget owns its own layout. */}
        <EntryDetailPanel label="What the user sees" theme={theme} cols={2} noPadding className={GENERIC_ROW_HEIGHT} headerRight={refreshButton ?? undefined}>
          <UserFacingPreviewWidget
            theme={theme}
            catalog={catalog}
            raw={preview.raw}
            processed={preview.processed ?? null}
            steps={steps as any}
            stepsReady={stepsReady}
            source={preview.source}
            userEntryId={preview.userEntryId}
          />
        </EntryDetailPanel>

        <EntryDetailPanel
          label="Actionable Evidence"
          theme={theme}
          cols={4}
          scrollable
          className={GENERIC_ROW_HEIGHT}
          headerRight={(busyBadge || evidenceHeaderExtras || refreshButton) ? <>{busyBadge}{evidenceHeaderExtras}{refreshButton}</> : undefined}
        >
          {evidenceBody ?? <ActionableEvidencePanel evidence={evidence} theme={theme} />}
        </EntryDetailPanel>

        {extraPanels}

        {appendedWidgets.map(({ instanceId, widgetId }) => {
          const def = library.find((w) => w.id === widgetId)
          if (!def) return null
          const Component = def.Component
          return (
            <EntryDetailPanel
              key={instanceId}
              label={def.label}
              theme={theme}
              cols={4}
              accent={def.accent}
              scrollable
              className={GENERIC_ROW_HEIGHT}
              onRemove={() => removeWidget(instanceId)}
            >
              <Component entry={entry} theme={theme} />
            </EntryDetailPanel>
          )
        })}

        <WidgetLibraryPicker theme={theme} onAdd={addWidget} library={library} colSpanClassName="col-span-full" />
      </div>
    </CollapsibleSection>
  )
}
